<div class="w-full max-w-full border border-gray-300 rounded-md overflow-hidden primary-font">
  <!-- Search Bar -->
  <div class="flex p-2 border-b border-gray-200 relative">
    <input 
      type="text" 
      [ngModel]="searchText()" 
      (ngModelChange)="onSearch($event)" 
      [placeholder]="config.searchPlaceholder || '' " 
      class="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base primary-font text-gray-700 font-normal"
    />
    @if (searchText()) {
      <button 
        (click)="clearSearch()" 
        class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        type="button"
        title="{{'overall_search.recently_viewed.clear' | translate}}"
      >
        <span class="text-2xl">×</span>
      </button>
    }
  </div>

  <!-- Multi-select actions -->
  @if (config?.multiSelect) {
    <div class="flex justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
      <div class="text-base primary-font text-gray-700 font-normal">
        {{ selectedItems().length }} {{'text.selected' | translate | lowercase}}
      </div>
      <div class="flex gap-2">
        <button 
          (click)="clearAll()" 
          class="text-blue-600 hover:text-blue-800 text-base primary-font font-normal" 
          type="button"
          title="{{'text.clear_selection' | translate}}"
        >
          {{'text.clear_all' | translate}}
        </button>
      </div>
    </div>
  }

  <!-- Options Container with Fixed Height -->
  <div class="h-60 flex flex-col">
    <!-- Initial Loading -->
    @if (isLoading()) {
      <div class="flex-1 flex items-center justify-center text-base primary-font text-gray-700 font-normal">
        <div class="text-center">
          <div class="flex justify-center mb-2">
          <output>
              <svg aria-hidden="true" class="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                  <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
              </svg>
              <span class="sr-only">Loading...</span>
          </output>
          </div>
          {{'text.loading' | translate}}
        </div>
      </div>
    }

    <!-- Error State -->
    @if (error() && !isLoading()) {
      <div class="flex-1 flex items-center justify-center text-base primary-font font-normal text-red-500">
        <div class="text-center">
          <div class="mb-2">{{ error() }}</div>
          <button 
            (click)="initializeComponent()" 
            class="text-blue-600 hover:text-blue-800 underline text-base primary-font font-normal" 
            type="button"
          >
            {{'edit.note.attachment.retry' | translate}}
          </button>
        </div>
      </div>
    }

    <!-- No Results -->
    @if (!isLoading() && !error() && filteredOptions().length === 0) {
      <div class="flex-1 flex items-center justify-center text-base primary-font text-gray-700 font-normal">
        {{'text.no_results_found' | translate}}
      </div>
    }

    <!-- Options -->
    @if (!isLoading() && !error()) {
      <div class="flex-1 overflow-y-auto overflow-x-hidden relative" (scroll)="onOptionsScroll($event)">
        @if (!(config.groupByKey || config.parentGroupKey)) {
          <!-- Non-grouped options list -->
          @for (option of filteredOptions(); track trackByFn($index, option)) {
            <div class="px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center transition-colors"
              [class.bg-blue-50]="isSelected(option)" (click)="toggleSelection(option)" [title]="getItemDisplayName(option)">
              @if (config?.multiSelect) {
              <div class="flex items-center w-full min-w-0">
                <input type="checkbox" [checked]="isSelected(option)" (click)="$event.stopPropagation()"
                  (change)="toggleSelection(option)"
                  class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                  [title]="getItemDisplayName(option)" />
                <div class="ml-3 text-base truncate primary-font text-gray-700 font-normal flex-1 min-w-0">
                  {{ getItemDisplayName(option) }}
                  @if (config.showId) {
                  <span class="ml-1 text-gray-400">({{ option[config.keyUid] }})</span>
                  }
                </div>
              </div>
              } @else {
                <div class="flex items-center w-full">
                  <input type="radio" [checked]="isSelected(option)" [name]="'option-radio'" (click)="$event.stopPropagation()"
                    (change)="toggleSelection(option)" class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    [title]="getItemDisplayName(option)" />
                  <div class="ml-3 text-base truncate primary-font text-gray-700 font-normal">
                    {{ getItemDisplayName(option) }}
                    @if (config.showId) {
                      <span class="ml-1 text-gray-400">({{ option[config.keyUid] }})</span>
                    }
                  </div>
                </div>
              }
            </div>
          }
        } @else {
          <!-- Grouped options list - For job statuses grouped by category -->
          @for (group of groupedOptionsList(); track trackByGroupFn($index, group)) {
            <div class="px-3 py-2 bg-gray-100 border-b border-gray-200 text-base font-medium text-gray-700 primary-font"
              [title]="group.groupLabel">
              {{ group.groupLabel }}
            </div>
            @for (option of group.items; track trackByFn($index, option)) {
              <div class="px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center transition-colors pl-5"
                [class.bg-blue-50]="isSelected(option)" (click)="toggleSelection(option)" [title]="getItemDisplayName(option)">
                @if (config?.multiSelect) {
                <div class="flex items-center w-full min-w-0">
                  <input type="checkbox" [checked]="isSelected(option)" (click)="$event.stopPropagation()"
                    (change)="toggleSelection(option)"
                    class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                    [title]="getItemDisplayName(option)" />
                  <div class="ml-3 text-base truncate primary-font text-gray-700 font-normal flex-1 min-w-0">
                    {{ getItemDisplayName(option) }}
                    @if (config.showId) {
                    <span class="ml-1 text-gray-400">({{ option[config.keyUid] }})</span>
                    }
                  </div>
                </div>
                } @else {
                  <div class="flex items-center w-full">
                    <input type="radio" [checked]="isSelected(option)" [name]="'option-radio'" (click)="$event.stopPropagation()"
                      (change)="toggleSelection(option)" class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      [title]="getItemDisplayName(option)" />
                    <div class="ml-3 text-base truncate primary-font text-gray-700 font-normal">
                      {{ getItemDisplayName(option) }}
                      @if (config.showId) {
                        <span class="ml-1 text-gray-400">({{ option[config.keyUid] }})</span>
                      }
                    </div>
                  </div>
                }
              </div>
            }
          }
        }

        <!-- Loading More Indicator - At the bottom of scrollable content -->
        @if (loadingMore()) {
          <div class="px-3 py-4 border-t border-gray-200 bg-gray-50">
            <div class="flex justify-center items-center space-x-2">
              <output>
                <svg aria-hidden="true" class="w-5 h-5 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                  viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor" />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill" />
                </svg>
                <span class="sr-only">Loading...</span>
              </output>
              <span class="text-sm primary-font text-gray-600 font-normal">{{'text.loading' | translate}}</span>
            </div>
          </div>
        }
      </div>
    }
  </div>

  <!-- Selected Summary -->
  @if (config?.showSelectedSummary && ((config.multiSelect && selectedItems().length > 0) || (!config.multiSelect && selectedItem()))) {
    <div class="border-t border-gray-200 px-3 py-2 bg-gray-50">
      <div class="mb-1 text-base font-medium text-gray-500 primary-font">{{'text.selected' | translate}}:</div>
      @if (config.multiSelect) {
        <div class="flex flex-wrap gap-1 overflow-y-auto zuper-scrollbar max-h-32">
          @for (item of selectedItems(); track trackByFn($index, item)) {
            <span 
              class="inline-flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded text-base primary-font font-normal"
              [title]="getItemDisplayNameWithGroup(item)"
            >
              {{ getItemDisplayNameWithGroup(item) }}
              <button 
                class="ml-1 text-blue-600 hover:text-blue-800 text-2xl" 
                (click)="toggleSelection(item); $event.stopPropagation();" 
                type="button"  
              >
                ×
              </button>
            </span>
          }
        </div>
      } @else {
        <div class="text-base primary-font text-gray-700 font-normal">
          {{ getItemDisplayNameWithGroup(selectedItem()) }}
          @if (config.showId) {
            <span class="ml-1 text-gray-400">({{ selectedItem()[config.keyUid] }})</span>
          }
        </div>
      }
    </div>
  }
</div>